const CELEBRATE_STYLE_ID = 'jord-celebrate-style';

// Keep the file reference matching the real casing on disk (Linux hosts are
// case-sensitive) — the asset lives at packages/frontend/public/jordthumbs.JPG.
const IMAGE_SRC = '/jordthumbs.JPG';

/**
 * A self-contained celebration: a photo spins in from small, "plasters" itself
 * dead-center with a GOOD JOB caption below, holds briefly, then fades out over
 * 0.75s. The whole visible sequence runs for ~2s and cleans up after itself, so
 * it needs no React state or external library.
 *
 * The image is decoded before the sequence starts, so a large file can't get
 * removed before it has a chance to paint.
 */
export function celebrate(): void {
  if (typeof document === 'undefined') return;

  console.debug('[celebrate] triggered');
  ensureStyles();

  const img = new Image();
  img.src = IMAGE_SRC;

  const start = () => run(img);
  // decode() resolves once the bitmap is ready to paint; fall back to onload /
  // an immediate run so we never silently skip the celebration.
  if (typeof img.decode === 'function') {
    img.decode().then(start).catch(start);
  } else if (!img.complete) {
    img.onload = start;
    img.onerror = start;
  } else {
    start();
  }
}

function run(img: HTMLImageElement): void {
  console.debug('[celebrate] rendering overlay');
  const overlay = document.createElement('div');
  overlay.className = 'jord-celebrate-overlay';

  const stack = document.createElement('div');
  stack.className = 'jord-celebrate-stack';

  img.alt = 'Good job!';
  img.className = 'jord-celebrate-img';

  const caption = document.createElement('div');
  caption.className = 'jord-celebrate-caption';
  caption.textContent = 'GOOD JOB';

  stack.append(img, caption);
  overlay.append(stack);
  document.body.append(overlay);

  // Total lifetime: 2s. The fade-out (last 0.75s) is baked into the keyframes.
  window.setTimeout(() => overlay.remove(), 2000);
}

function ensureStyles(): void {
  if (document.getElementById(CELEBRATE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = CELEBRATE_STYLE_ID;
  style.textContent = `
    .jord-celebrate-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      /* whole thing fades out over the final 0.75s (1.25s-2s) */
      animation: jord-celebrate-out 2s linear forwards;
    }
    .jord-celebrate-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }
    .jord-celebrate-img {
      width: min(42vw, 340px);
      height: auto;
      border-radius: 16px;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
      background: #fff;
      /* spin/scale in over 0-0.9s, then hold */
      animation: jord-celebrate-spin 0.9s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards;
    }
    .jord-celebrate-caption {
      font-size: clamp(32px, 7vw, 72px);
      font-weight: 800;
      letter-spacing: 0.06em;
      color: #fff;
      text-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
      -webkit-text-stroke: 2px rgba(0, 0, 0, 0.25);
      opacity: 0;
      /* pop in once the photo has landed */
      animation: jord-celebrate-pop 0.35s ease-out 0.7s forwards;
    }
    @keyframes jord-celebrate-spin {
      0% { transform: scale(0.1) rotate(0deg); opacity: 0; }
      60% { opacity: 1; }
      100% { transform: scale(1) rotate(1080deg); opacity: 1; }
    }
    @keyframes jord-celebrate-pop {
      0% { transform: scale(0.6); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes jord-celebrate-out {
      0%, 62.5% { opacity: 1; }
      100% { opacity: 0; }
    }
  `;
  document.head.append(style);
}

// Manual test hook: run `celebrate()` from the browser console to fire the
// animation directly, independent of the Smart Fill success path.
if (typeof window !== 'undefined') {
  (window as unknown as { celebrate?: () => void }).celebrate = celebrate;
}
